import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Clock, Banknote, CalendarDays, Loader2, Save, AlertTriangle, Ban, LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react";
import { CoPilotFieldHelp } from "@/components/CoPilotFieldHelp";
import { format } from "date-fns";

const DAYS = [
  { value: "MONDAY", short: "Mon" },
  { value: "TUESDAY", short: "Tue" },
  { value: "WEDNESDAY", short: "Wed" },
  { value: "THURSDAY", short: "Thu" },
  { value: "FRIDAY", short: "Fri" },
  { value: "SATURDAY", short: "Sat" },
  { value: "SUNDAY", short: "Sun" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = i.toString().padStart(2, "0");
  return { value: `${h}:00`, label: `${h}:00` };
});

const DURATIONS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
];

const PEAK_HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function CourtManage() {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Availability rule form
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editRuleId, setEditRuleId] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    days_of_week: [], start_time: "08:00", end_time: "22:00",
    slot_duration_minutes: 60, effective_from: format(new Date(), "yyyy-MM-dd"), effective_to: "",
  });
  const [effectiveFromDate, setEffectiveFromDate] = useState(new Date());
  const [deleteRuleDialog, setDeleteRuleDialog] = useState(null);

  // Pricing form
  const [pricingForm, setPricingForm] = useState({
    base_rate: 500, peak_rate: 700, peak_hours: [17, 18, 19, 20],
    weekend_multiplier: 1.2, currency: "PHP",
  });

  // Blackout form
  const [showBlackoutForm, setShowBlackoutForm] = useState(false);
  const [blackoutForm, setBlackoutForm] = useState({
    start_date: format(new Date(), "yyyy-MM-dd"), end_date: format(new Date(), "yyyy-MM-dd"),
    start_time: "00:00", end_time: "23:59", reason: "", all_day: true,
  });
  const [deleteBlackoutDialog, setDeleteBlackoutDialog] = useState(null);
  const [diagnosticDate, setDiagnosticDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

  // Slot Simulation
  const [simDate, setSimDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [simData, setSimData] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const fetchResource = useCallback(async () => {
    try {
      const res = await api.get(`/resources/${resourceId}`);
      setResource(res.data);
      if (res.data.pricing) {
        setPricingForm({
          base_rate: res.data.pricing.base_rate || 500,
          peak_rate: res.data.pricing.peak_rate || 700,
          peak_hours: res.data.pricing.peak_hours || [],
          weekend_multiplier: res.data.pricing.weekend_multiplier || 1.0,
          currency: res.data.pricing.currency || "PHP",
        });
      }
    } catch { toast.error("Court not found"); navigate("/provider/venues"); }
    setLoading(false);
  }, [resourceId, navigate]);

  useEffect(() => { fetchResource(); }, [fetchResource]);

  const fetchDiagnostics = async (targetDate = diagnosticDate) => {
    setDiagnosticsLoading(true);
    try {
      const res = await api.get(`/resources/${resourceId}/availability-diagnostics`, { params: { date: targetDate } });
      setDiagnostics(res.data);
    } catch {
      toast.error("Failed to load availability diagnostics");
    }
    setDiagnosticsLoading(false);
  };

  useEffect(() => { if (resourceId) fetchDiagnostics(); }, [resourceId]);

  const fetchSimulation = async (targetDate = simDate) => {
    setSimLoading(true);
    try {
      const res = await api.get(`/resources/${resourceId}/simulate-slots`, { params: { date: targetDate } });
      setSimData(res.data);
    } catch {
      toast.error("Failed to load slot simulation");
    }
    setSimLoading(false);
  };

  useEffect(() => { if (resourceId) fetchSimulation(); }, [resourceId]);

  // Toggle day in rule form
  const toggleDay = (day) => {
    setRuleForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter(d => d !== day)
        : [...f.days_of_week, day],
    }));
  };

  // Toggle peak hour
  const togglePeakHour = (hour) => {
    setPricingForm(f => ({
      ...f,
      peak_hours: f.peak_hours.includes(hour)
        ? f.peak_hours.filter(h => h !== hour)
        : [...f.peak_hours, hour].sort((a, b) => a - b),
    }));
  };

  // Add availability rule
  const handleAddRule = async () => {
    if (ruleForm.days_of_week.length === 0) { toast.error("Select at least one day"); return; }
    if (!ruleForm.start_time || !ruleForm.end_time) { toast.error("Set start and end times"); return; }
    if (ruleForm.start_time >= ruleForm.end_time) { toast.error("End time must be after start time"); return; }
    setSaving(true);
    try {
      const payload = {
        ...ruleForm,
        effective_from: ruleForm.effective_from,
        effective_to: ruleForm.effective_to || null,
      };
      await api.post(`/resources/${resourceId}/availability-rules`, payload);
      toast.success("Availability rule added!");
      setShowRuleForm(false);
      setRuleForm({ days_of_week: [], start_time: "08:00", end_time: "22:00", slot_duration_minutes: 60,
        effective_from: format(new Date(), "yyyy-MM-dd"), effective_to: "" });
      fetchResource();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add rule"); }
    setSaving(false);
  };

  const handleEditRule = (rule) => {
    setEditRuleId(rule.rule_id);
    setRuleForm({
      days_of_week: rule.days_of_week || [],
      start_time: rule.start_time,
      end_time: rule.end_time,
      slot_duration_minutes: rule.slot_duration_minutes,
      effective_from: rule.effective_from,
      effective_to: rule.effective_to || "",
    });
    setShowRuleForm(true);
  };

  const handleSaveRule = async () => {
    if (!editRuleId) return handleAddRule();
    if (ruleForm.days_of_week.length === 0) { toast.error("Select at least one day"); return; }
    setSaving(true);
    try {
      await api.put(`/resources/${resourceId}/availability-rules/${editRuleId}`, { ...ruleForm, effective_to: ruleForm.effective_to || null });
      toast.success("Availability rule updated!");
      setShowRuleForm(false);
      setEditRuleId(null);
      fetchResource();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update rule"); }
    setSaving(false);
  };

  // Delete availability rule
  const handleDeleteRule = async () => {
    if (!deleteRuleDialog) return;
    setSaving(true);
    try {
      await api.delete(`/resources/${resourceId}/availability-rules/${deleteRuleDialog}`);
      toast.success("Rule deleted");
      setDeleteRuleDialog(null);
      fetchResource();
    } catch (err) { toast.error("Failed to delete rule"); }
    setSaving(false);
  };

  // Save pricing
  const handleSavePricing = async () => {
    if (pricingForm.base_rate <= 0) { toast.error("Base rate must be positive"); return; }
    setSaving(true);
    try {
      await api.post(`/resources/${resourceId}/pricing`, pricingForm);
      toast.success("Pricing saved!");
      fetchResource();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save pricing"); }
    setSaving(false);
  };

  // Add blackout
  const handleAddBlackout = async () => {
    if (!blackoutForm.start_date || !blackoutForm.end_date) { toast.error("Select dates"); return; }
    if (blackoutForm.start_date > blackoutForm.end_date) { toast.error("End date must be after start date"); return; }
    setSaving(true);
    try {
      await api.post(`/resources/${resourceId}/blackouts`, blackoutForm);
      toast.success("Blackout period added!");
      setShowBlackoutForm(false);
      setBlackoutForm({ start_date: format(new Date(), "yyyy-MM-dd"), end_date: format(new Date(), "yyyy-MM-dd"),
        start_time: "00:00", end_time: "23:59", reason: "", all_day: true });
      fetchResource();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add blackout"); }
    setSaving(false);
  };

  // Delete blackout
  const handleDeleteBlackout = async () => {
    if (!deleteBlackoutDialog) return;
    setSaving(true);
    try {
      await api.delete(`/resources/${resourceId}/blackouts/${deleteBlackoutDialog}`);
      toast.success("Blackout removed");
      setDeleteBlackoutDialog(null);
      fetchResource();
    } catch { toast.error("Failed to delete"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!resource) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="court-manage-page">
      <button data-testid="back-to-venues" onClick={() => navigate("/provider/venues")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" />Back to Venues
      </button>

      {/* Court Header */}
      <div className="bg-white border-2 border-slate-100 p-6 mb-8" data-testid="court-header">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-heading uppercase tracking-[0.15em] text-muted-foreground mb-1">{resource.venue_name}</p>
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">{resource.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={`sport-${resource.sport_type} text-[10px] font-heading uppercase tracking-wider`}>{resource.sport_type}</Badge>
              <span className="text-sm text-muted-foreground capitalize">{resource.attributes?.indoor ? "Indoor" : "Outdoor"} - {resource.attributes?.surface || "Standard"}</span>
            </div>
          </div>
          <Badge variant="outline" className={`text-xs ${resource.status === 'ACTIVE' ? 'text-green-600 border-green-200' : 'text-red-600 border-red-200'}`}>{resource.status}</Badge>
        </div>
      </div>

      {/* Active Warning Banner */}
      {resource.status === "ACTIVE" && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-8 flex items-start gap-3" data-testid="active-warning-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-heading font-bold text-sm text-amber-800 uppercase tracking-wide">This court is currently ACTIVE</p>
            <p className="text-sm text-amber-700 mt-1">Changing availability rules, pricing, or blackout periods may affect existing and future bookings. Please validate all changes carefully before saving.</p>
          </div>
        </div>
      )}

      <section className="mb-10" data-testid="availability-diagnostics-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-court-orange" />
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Availability Diagnostics</h2>
          </div>
        </div>
        <div className="bg-white border-2 border-slate-100 p-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <Input type="date" value={diagnosticDate} onChange={(e) => setDiagnosticDate(e.target.value)} className="max-w-xs" data-testid="diagnostic-date-input" />
            <Button variant="outline" onClick={() => fetchDiagnostics(diagnosticDate)} data-testid="run-diagnostics-btn">Run Diagnostics</Button>
          </div>
          {diagnosticsLoading ? <div className="text-sm text-muted-foreground">Loading diagnostics…</div> : diagnostics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Active Rules</p><p className="font-heading text-xl font-bold">{diagnostics.active_rules_count}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Blackouts</p><p className="font-heading text-xl font-bold">{diagnostics.blackouts_count}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Bookings</p><p className="font-heading text-xl font-bold">{diagnostics.bookings_count}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Waitlist</p><p className="font-heading text-xl font-bold">{diagnostics.waitlist_count}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Date</p><p className="font-heading text-xl font-bold">{diagnostics.date}</p></div>
              </div>
              <div className="space-y-2">
                {diagnostics.diagnostics.map((item, idx) => (
                  <div key={idx} className={`rounded-lg border p-3 text-sm ${item.status === 'issue' ? 'bg-red-50 border-red-200 text-red-800' : item.status === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`} data-testid={`diagnostic-item-${item.type}`}>
                    <p className="font-medium capitalize">{item.type.replace(/_/g, ' ')}</p>
                    <p className="mt-1">{item.message}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Slot Simulation View ── */}
      <section className="mb-10" data-testid="slot-simulation-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-blue-600" />
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Slot Simulation</h2>
          </div>
        </div>
        <div className="bg-white border-2 border-slate-100 p-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0" data-testid="sim-prev-day"
                onClick={() => {
                  const prev = new Date(simDate);
                  prev.setDate(prev.getDate() - 1);
                  const nd = format(prev, "yyyy-MM-dd");
                  setSimDate(nd);
                  fetchSimulation(nd);
                }}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Input type="date" value={simDate} onChange={(e) => setSimDate(e.target.value)} className="max-w-[180px]" data-testid="sim-date-input" />
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0" data-testid="sim-next-day"
                onClick={() => {
                  const nxt = new Date(simDate);
                  nxt.setDate(nxt.getDate() + 1);
                  const nd = format(nxt, "yyyy-MM-dd");
                  setSimDate(nd);
                  fetchSimulation(nd);
                }}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchSimulation(simDate)} data-testid="run-simulation-btn">
              <LayoutGrid className="w-3 h-3 mr-1.5" />Simulate
            </Button>
            {simData && (
              <p className="text-xs text-muted-foreground">
                {simData.day_name}{simData.is_weekend ? " (Weekend)" : ""} · {simData.rules_applied} rule(s) applied
              </p>
            )}
          </div>

          {simLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="w-4 h-4 animate-spin" />Generating slots…
            </div>
          ) : simData ? (
            <>
              {/* Summary counters */}
              <div className="flex gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="text-xs font-heading uppercase tracking-wider text-emerald-800">{simData.available_count} Available</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-200">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-xs font-heading uppercase tracking-wider text-red-800">{simData.booked_count} Booked</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-300">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-500" />
                  <span className="text-xs font-heading uppercase tracking-wider text-slate-700">{simData.blackout_count} Blackout</span>
                </div>
                <div className="ml-auto text-xs text-muted-foreground self-center">{simData.total_slots} total slots</div>
              </div>

              {/* Slot grid */}
              {simData.slots.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-slate-200">
                  <LayoutGrid className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
                  <p className="font-heading text-sm text-muted-foreground uppercase">No slots for this date</p>
                  <p className="text-xs text-muted-foreground mt-1">{simData.message || "Add availability rules to generate bookable slots"}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="sim-slot-grid">
                  {simData.slots.map((slot, idx) => {
                    const bgClass = slot.status === "available"
                      ? "bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                      : slot.status === "booked"
                      ? "bg-red-50 border-red-200"
                      : "bg-slate-100 border-slate-300";
                    const textClass = slot.status === "available"
                      ? "text-emerald-800"
                      : slot.status === "booked"
                      ? "text-red-800"
                      : "text-slate-600";
                    return (
                      <div key={idx} className={`relative rounded-lg border p-2.5 transition-colors ${bgClass}`} data-testid={`sim-slot-${idx}`}>
                        <p className={`text-xs font-heading font-bold ${textClass}`}>
                          {slot.start.slice(11, 16)} - {slot.end.slice(11, 16)}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-[10px] font-medium uppercase ${textClass}`}>{slot.status}</span>
                          <span className="text-[10px] text-muted-foreground">₱{slot.rate}</span>
                        </div>
                        {slot.is_peak && (
                          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-court-orange" title="Peak hour" />
                        )}
                        {slot.has_waitlist && (
                          <span className="absolute top-1 right-4 w-1.5 h-1.5 rounded-full bg-amber-400" title="Waitlisted" />
                        )}
                        {slot.booking_info && (
                          <p className="text-[9px] text-red-600 mt-0.5 truncate">{slot.booking_info.player_name || slot.booking_info.booking_id}</p>
                        )}
                        {slot.status_detail && (
                          <p className="text-[9px] text-slate-500 mt-0.5 truncate">{slot.status_detail}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : null}
        </div>
      </section>

      <Separator className="mb-10" />

      {/* ── Availability Rules Section ── */}
      <section className="mb-10" data-testid="availability-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-court-orange" />
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Availability Rules</h2>
          </div>
          <CoPilotFieldHelp stepKey="availability" />
          <Button data-testid="add-rule-btn" onClick={() => setShowRuleForm(true)} size="sm"
            className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
            <Plus className="w-3 h-3 mr-1" />Add Rule
          </Button>
        </div>

        {/* Existing Rules */}
        {resource.availability_rules?.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-slate-200">
            <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="font-heading text-sm text-muted-foreground uppercase">No availability rules set</p>
            <p className="text-xs text-muted-foreground mt-1">Add rules to define when this court can be booked</p>
          </div>
        ) : (
          <div className="space-y-3">
            {resource.availability_rules?.map((rule) => (
              <div key={rule.rule_id} className="bg-white border-2 border-slate-100 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`rule-${rule.rule_id}`}>
                <div className="flex-1">
                  <div className="flex gap-1.5 flex-wrap mb-2">
                    {rule.days_of_week?.map(d => (
                      <span key={d} className="px-2 py-0.5 bg-slate-100 text-xs font-heading uppercase tracking-wide">{d.slice(0, 3)}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 font-medium">
                      <Clock className="w-3 h-3 text-court-orange" />{rule.start_time} - {rule.end_time}
                    </span>
                    <span className="text-muted-foreground">{rule.slot_duration_minutes} min slots</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    From {rule.effective_from}{rule.effective_to ? ` to ${rule.effective_to}` : " (ongoing)"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`edit-rule-${rule.rule_id}`} variant="outline" size="sm" onClick={() => handleEditRule(rule)} className="rounded-full border-2">Edit</Button>
                  <Button data-testid={`delete-rule-${rule.rule_id}`} variant="outline" size="sm"
                    onClick={() => setDeleteRuleDialog(rule.rule_id)}
                    className="rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="w-3 h-3 mr-1" />Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Rule Form */}
        {showRuleForm && (
          <div className="mt-4 bg-white border-2 border-court-orange/30 p-6" data-testid="add-rule-form">
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-court-orange mb-4">{editRuleId ? 'Edit Availability Rule' : 'New Availability Rule'}</h3>

            {/* Days of Week */}
            <div className="mb-5">
              <Label className="text-xs font-heading uppercase tracking-wider mb-2 block">Days of Week</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map(day => (
                  <label key={day.value} data-testid={`day-${day.value.toLowerCase()}`}
                    className={`flex items-center gap-2 px-3 py-2 border-2 cursor-pointer transition-colors text-sm font-heading uppercase
                      ${ruleForm.days_of_week.includes(day.value) ? 'border-court-orange bg-court-orange/5 text-court-orange' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                    <Checkbox checked={ruleForm.days_of_week.includes(day.value)}
                      onCheckedChange={() => toggleDay(day.value)} className="sr-only" />
                    {day.short}
                  </label>
                ))}
              </div>
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Start Time</Label>
                <Select value={ruleForm.start_time} onValueChange={v => setRuleForm({...ruleForm, start_time: v})}>
                  <SelectTrigger data-testid="rule-start-time" className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">End Time</Label>
                <Select value={ruleForm.end_time} onValueChange={v => setRuleForm({...ruleForm, end_time: v})}>
                  <SelectTrigger data-testid="rule-end-time" className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Slot Duration</Label>
                <Select value={String(ruleForm.slot_duration_minutes)} onValueChange={v => setRuleForm({...ruleForm, slot_duration_minutes: parseInt(v)})}>
                  <SelectTrigger data-testid="rule-slot-duration" className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{DURATIONS.map(d => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Effective From</Label>
                <Input data-testid="rule-effective-from" type="date" value={ruleForm.effective_from}
                  onChange={e => setRuleForm({...ruleForm, effective_from: e.target.value})}
                  className="h-11 rounded-none border-2 mt-1" />
              </div>
            </div>

            <div className="flex gap-3">
              <Button data-testid="save-rule-btn" onClick={handleSaveRule} disabled={saving}
                className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase px-6 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-1" />}
                {editRuleId ? 'Save Rule' : 'Add Rule'}
              </Button>
              <Button data-testid="cancel-rule-btn" variant="outline" onClick={() => { setShowRuleForm(false); setEditRuleId(null); }} className="rounded-full border-2">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </section>

      <Separator className="mb-10" />

      {/* ── Blackout Periods Section ── */}
      <section className="mb-10" data-testid="blackout-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-500" />
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Blackout Periods</h2>
          </div>
          <CoPilotFieldHelp stepKey="blackouts" />
          <Button data-testid="add-blackout-btn" onClick={() => setShowBlackoutForm(true)} size="sm"
            className="bg-red-500 hover:bg-red-600 text-white rounded-full font-heading uppercase text-xs px-5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
            <Plus className="w-3 h-3 mr-1" />Add Blackout
          </Button>
        </div>

        {/* Existing Blackouts */}
        {(!resource.blackouts || resource.blackouts.length === 0) ? (
          <div className="text-center py-8 border-2 border-dashed border-slate-200">
            <Ban className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
            <p className="font-heading text-sm text-muted-foreground uppercase">No blackout periods</p>
            <p className="text-xs text-muted-foreground mt-1">Block dates for maintenance, holidays, or private events</p>
          </div>
        ) : (
          <div className="space-y-2">
            {resource.blackouts.map(bl => (
              <div key={bl.blackout_id} className="bg-white border-2 border-red-100 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`blackout-${bl.blackout_id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <CalendarDays className="w-3.5 h-3.5 text-red-500" />
                      {bl.start_date === bl.end_date ? bl.start_date : `${bl.start_date} to ${bl.end_date}`}
                    </span>
                    {bl.all_day ? (
                      <Badge className="bg-red-100 text-red-700 text-[10px] font-heading uppercase">All Day</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />{bl.start_time} - {bl.end_time}
                      </span>
                    )}
                  </div>
                  {bl.reason && <p className="text-sm text-muted-foreground">{bl.reason}</p>}
                </div>
                <Button data-testid={`delete-blackout-${bl.blackout_id}`} variant="outline" size="sm"
                  onClick={() => setDeleteBlackoutDialog(bl.blackout_id)}
                  className="rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="w-3 h-3 mr-1" />Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add Blackout Form */}
        {showBlackoutForm && (
          <div className="mt-4 bg-white border-2 border-red-200/50 p-6" data-testid="add-blackout-form">
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-red-500 mb-4">New Blackout Period</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Start Date</Label>
                <Input data-testid="blackout-start-date" type="date" value={blackoutForm.start_date}
                  onChange={e => setBlackoutForm({...blackoutForm, start_date: e.target.value})}
                  className="h-11 rounded-none border-2 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">End Date</Label>
                <Input data-testid="blackout-end-date" type="date" value={blackoutForm.end_date}
                  onChange={e => setBlackoutForm({...blackoutForm, end_date: e.target.value})}
                  className="h-11 rounded-none border-2 mt-1" />
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-3 mb-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="blackout-allday-toggle">
                  <Checkbox checked={blackoutForm.all_day}
                    onCheckedChange={v => setBlackoutForm({...blackoutForm, all_day: v})} />
                  <span className="text-sm font-medium">Block entire day(s)</span>
                </label>
              </div>
              {!blackoutForm.all_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider">Start Time</Label>
                    <Input data-testid="blackout-start-time" type="time" value={blackoutForm.start_time}
                      onChange={e => setBlackoutForm({...blackoutForm, start_time: e.target.value})}
                      className="h-11 rounded-none border-2 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider">End Time</Label>
                    <Input data-testid="blackout-end-time" type="time" value={blackoutForm.end_time}
                      onChange={e => setBlackoutForm({...blackoutForm, end_time: e.target.value})}
                      className="h-11 rounded-none border-2 mt-1" />
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4">
              <Label className="text-xs font-heading uppercase tracking-wider">Reason (optional)</Label>
              <Input data-testid="blackout-reason" value={blackoutForm.reason}
                onChange={e => setBlackoutForm({...blackoutForm, reason: e.target.value})}
                placeholder="e.g. Court maintenance, Holiday closure, Private event"
                className="h-11 rounded-none border-2 mt-1" />
            </div>

            <div className="flex gap-3">
              <Button data-testid="save-blackout-btn" onClick={handleAddBlackout} disabled={saving}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full font-heading uppercase px-6 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-1" />}
                Block Dates
              </Button>
              <Button data-testid="cancel-blackout-btn" variant="outline" onClick={() => setShowBlackoutForm(false)} className="rounded-full border-2">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </section>

      <Separator className="mb-10" />

      {/* ── Pricing Section ── */}
      <section data-testid="pricing-section">
        <div className="flex items-center gap-2 mb-4">
          <Banknote className="w-5 h-5 text-lime-volt" />
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Pricing</h2>
          <CoPilotFieldHelp stepKey="court_pricing" />
        </div>

        <div className="bg-white border-2 border-slate-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label className="text-xs font-heading uppercase tracking-wider">Base Rate (₱ per slot)</Label>
              <Input data-testid="pricing-base-rate" type="number" step="0.01" value={pricingForm.base_rate}
                onChange={e => setPricingForm({...pricingForm, base_rate: parseFloat(e.target.value) || 0})}
                className="h-11 rounded-none border-2 mt-1" />
              <p className="text-[10px] text-muted-foreground mt-1">Regular rate charged per time slot</p>
            </div>
            <div>
              <Label className="text-xs font-heading uppercase tracking-wider">Peak Rate (₱ per slot)</Label>
              <Input data-testid="pricing-peak-rate" type="number" step="0.01" value={pricingForm.peak_rate || ""}
                onChange={e => setPricingForm({...pricingForm, peak_rate: parseFloat(e.target.value) || null})}
                className="h-11 rounded-none border-2 mt-1" />
              <p className="text-[10px] text-muted-foreground mt-1">Rate during peak hours (leave empty to use base rate)</p>
            </div>
            <div>
              <Label className="text-xs font-heading uppercase tracking-wider">Weekend Multiplier</Label>
              <Input data-testid="pricing-weekend-mult" type="number" step="0.1" min="1" value={pricingForm.weekend_multiplier}
                onChange={e => setPricingForm({...pricingForm, weekend_multiplier: parseFloat(e.target.value) || 1.0})}
                className="h-11 rounded-none border-2 mt-1" />
              <p className="text-[10px] text-muted-foreground mt-1">Multiply rate by this on weekends (1.0 = no change, 1.2 = +20%)</p>
            </div>
            <div>
              <Label className="text-xs font-heading uppercase tracking-wider">Currency</Label>
              <Select value={pricingForm.currency} onValueChange={v => setPricingForm({...pricingForm, currency: v})}>
                <SelectTrigger data-testid="pricing-currency" className="h-11 rounded-none border-2 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PHP">₱ Philippine Peso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Peak Hours */}
          <div className="mb-6">
            <Label className="text-xs font-heading uppercase tracking-wider mb-2 block">Peak Hours</Label>
            <p className="text-[10px] text-muted-foreground mb-3">Select hours considered "peak" (higher rate applies)</p>
            <div className="flex flex-wrap gap-1.5">
              {PEAK_HOURS.map(h => (
                <button key={h} data-testid={`peak-hour-${h}`}
                  onClick={() => togglePeakHour(h)}
                  className={`w-12 h-9 text-xs font-heading border-2 transition-colors
                    ${pricingForm.peak_hours.includes(h) ? 'border-court-orange bg-court-orange/10 text-court-orange font-bold' : 'border-slate-200 text-slate-400 hover:border-slate-300'}`}>
                  {h.toString().padStart(2, "0")}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Preview */}
          <div className="bg-slate-50 p-4 mb-6">
            <h4 className="font-heading text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Pricing Preview</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Weekday Regular</p>
                <p className="font-heading text-lg font-bold">₱{pricingForm.base_rate?.toFixed(0)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Weekday Peak</p>
                <p className="font-heading text-lg font-bold text-court-orange">₱{(pricingForm.peak_rate || pricingForm.base_rate)?.toFixed(0)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Weekend Regular</p>
                <p className="font-heading text-lg font-bold">₱{(pricingForm.base_rate * pricingForm.weekend_multiplier)?.toFixed(0)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Weekend Peak</p>
                <p className="font-heading text-lg font-bold text-court-orange">₱{((pricingForm.peak_rate || pricingForm.base_rate) * pricingForm.weekend_multiplier)?.toFixed(0)}</p>
              </div>
            </div>
          </div>

          <Button data-testid="save-pricing-btn" onClick={handleSavePricing} disabled={saving}
            className="bg-lime-volt hover:bg-lime-volt/90 text-black rounded-full font-heading uppercase px-8 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Pricing
          </Button>
        </div>
      </section>

      {/* Delete Rule Dialog */}
      <Dialog open={!!deleteRuleDialog} onOpenChange={() => setDeleteRuleDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-rule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />Delete Rule
            </DialogTitle>
            <DialogDescription>This will remove the availability rule. Existing bookings won't be affected.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteRuleDialog(null)} className="flex-1 rounded-full border-2">Keep Rule</Button>
            <Button data-testid="confirm-delete-rule-btn" onClick={handleDeleteRule} disabled={saving}
              className="flex-1 bg-destructive hover:bg-destructive/90 text-white rounded-full font-heading uppercase">
              {saving ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Blackout Dialog */}
      <Dialog open={!!deleteBlackoutDialog} onOpenChange={() => setDeleteBlackoutDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-blackout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />Remove Blackout
            </DialogTitle>
            <DialogDescription>This will unblock the dates and make slots bookable again.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteBlackoutDialog(null)} className="flex-1 rounded-full border-2">Keep Blackout</Button>
            <Button data-testid="confirm-delete-blackout-btn" onClick={handleDeleteBlackout} disabled={saving}
              className="flex-1 bg-destructive hover:bg-destructive/90 text-white rounded-full font-heading uppercase">
              {saving ? "Removing..." : "Remove"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
